import { Avatar, Box, Dialog, IconButton, Typography } from '@mui/material'
import { CustomLoadingOverlay, sortColDefs } from 'components/common/AgGridUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import AddEditAlertGroupsModal from './AddEditAlertGroupsModal'
import { AgGridReact } from 'ag-grid-react'
import AlertGroupRibbon from './AlertGroupsRibbon'
import AlertsModal from 'components/Alerts/AlertsModal'
import CloseIcon from '@mui/icons-material/Close'
import ConfirmDialog from 'components/common/ConfirmDialog'
import ResetFiltersButton from 'components/common/ResetFiltersButton'
import SearchBarWithRecent from 'components/common/SearchBarWithRecent'
import StatusSnackbar from 'components/common/StatusSnackbar'
import { addItemToLS } from 'utils/localStorage'
import { appColors } from 'utils'
import useAlertGroups from 'components/common/hooks/useAlertGroups'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import useObjectsOfInterest from 'components/common/hooks/useObjectsOfInterest'
import useOrgIcons from 'components/common/hooks/useOrgIcons'
import useUsers from 'components/common/hooks/useUsers'

const LS_KEY = 'alertGroupsModal'

const AlertGroupsModal = ({ organization, onCancel }) => {
  const gridApi = useRef(null)
  const _isMounted = useRef(false)

  const [searchText, setSearchText] = useState('')
  const [status, setStatus] = useState({ show: false, severity: 'info', message: '' })
  const [confirm, setConfirm] = useState({ show: false, title: '' })
  const [filterApplied, setFilterApplied] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [showAlerts, setShowAlerts] = useState(false)
  const [groupModal, setGroupModal] = useState({
    open: false,
    mode: 'add', // 'add' | 'edit'
    data: null,
  })

  const { getUnitsForOrg } = useUsers()
  const { dropDowns } = useObjectsOfInterest()
  const { getCompanyIcon } = useOrgIcons()
  const { userRole } = useInnovaAuth()
  const { getAgGridTheme, searchBarStyle, getActionBarColor, getBackColor, getWindowBarColor } = useInnovaTheme()

  const {
    alertGroups,
    isLoading,
    isAdding,
    isDeleting,
    isUpdating,
    fetchAlertGroups,
    addAlertGroup,
    updateAlertGroup,
    deleteAlertGroup,
  } = useAlertGroups()

  useEffect(() => {
    _isMounted.current = true
    fetchAlertGroups(organization)

    return () => {
      gridApi.current = null
      _isMounted.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const showResult = (res) => {
    setStatus({
      show: true,
      severity: res.success ? 'success' : 'error',
      message: res.message,
    })
  }

  const autoSizeColumns = useCallback(() => {
    const api = gridApi.current
    if (!api || api.isDestroyed()) return

    api.autoSizeAllColumns()
  }, [])

  const leftAlignCell = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      overflow: 'hidden',
    }),
    [],
  )

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      sortable: true,
      editable: false,
      autoHeight: true,
      cellStyle: leftAlignCell,
      headerClass: 'header-no-padding',
      filter: 'agSetColumnFilter',
      filterParams: {
        excelMode: 'windows',
      },
    }
  }, [leftAlignCell])

  const onFirstDataRendered = (params) => {
    if (gridApi.current) {
      gridApi.current.onFilterChanged()
    }

    autoSizeColumns()
  }

  const saveColumnState = useCallback(() => {
    const api = gridApi.current
    if (!api || api.isDestroyed()) return

    const colLayout = api.getColumnState()
    if (colLayout) {
      addItemToLS(LS_KEY, 'colLayout', colLayout)
    }
  }, [])

  const gridOptions = useMemo(
    () => ({
      rowSelection: {
        mode: 'singleRow',
        enableClickSelection: true,
        checkboxes: false,
      },
      onDragStopped: saveColumnState,
      onColumnVisible: saveColumnState,
      onExpandOrCollapseAll: autoSizeColumns,
      loadingOverlayComponent: CustomLoadingOverlay,
    }),
    [saveColumnState, autoSizeColumns],
  )

  const getContextMenuItems = (params) => {
    return [
      {
        name: 'Reset columns',
        disabled: false,
        action: () => {
          gridApi.current.resetColumnState()
          addItemToLS(LS_KEY, 'colLayout', null)
        },
        icon: '<span class="iconify" data-icon="carbon:reset" data-width="20" style="color:#4BB2F9"></span>',
        cssClasses: ['leftAlign'],
      },
      'copy',
    ]
  }

  const onFilterRemoved = () => {
    setSearchText('')
  }

  let columnDefs = useMemo(
    () => [
      {
        field: 'icon',
        colId: 'icon',
        headerName: '',
        width: 80,
        resizable: false,
        filter: null,
        sortable: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        headerComponent: ResetFiltersButton,
        headerComponentParams: {
          filterApplied: filterApplied,
          gridApi: gridApi,
          onFilterRemoved: onFilterRemoved,
        },
        pinned: 'left',
        lockPosition: 'left',
        cellRenderer: (params) => {
          if (typeof params?.data?.organization !== 'string') return null

          return (
            <Box
              sx={{
                margin: 'auto',
                width: 35,
                height: 35,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'lightgrey',
                borderRadius: '50%',
              }}>
              <Avatar
                alt={params?.data?.organization}
                sx={{ width: '30px', height: '30px', marginLeft: '1px' }}
                src={getCompanyIcon(params?.data?.organization)}
              />
            </Box>
          )
        },
      },
      {
        field: 'groupName',
        colId: 'groupName',
        headerName: 'Group Name',
        cellStyle: leftAlignCell,
      },
      {
        field: 'organization',
        colId: 'organization',
        headerName: 'Organization',
        cellStyle: leftAlignCell,
      },
      {
        field: 'groupDetails.distributionList',
        colId: 'groupDetails.distributionList',
        headerName: 'Recipients',
        minWidth: 200,
        maxWidth: 600,
        autoHeight: true,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          padding: '6px',
        },
        cellRenderer: (params) => {
          if (!params.value) return ''
          if (!Array.isArray(params.value)) return ''

          return (
            <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
              {params.value?.map((row, index) => (
                <div key={index}>{row}</div>
              ))}
            </div>
          )
        },
      },
      {
        field: 'groupDetails.unitPrefs',
        colId: 'groupDetails.unitPrefs',
        headerName: 'Units',
        cellStyle: leftAlignCell,
      },
      {
        field: 'groupDetails.rigs',
        colId: 'groupDetails.rigs',
        headerName: 'Rigs',
        minWidth: 200,
        maxWidth: 600,
        autoHeight: true,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          padding: '6px',
        },
        cellRenderer: (params) => {
          if (!params.value) return ''
          if (!Array.isArray(params.value)) return ''

          return (
            <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
              {params.value?.map((row, index) => (
                <div key={index}>{row}</div>
              ))}
            </div>
          )
        },
      },
      {
        field: 'groupDetails.operators',
        colId: 'groupDetails.operators',
        headerName: 'Operators',
        minWidth: 200,
        maxWidth: 600,
        autoHeight: true,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          padding: '6px',
        },
        cellRenderer: (params) => {
          if (!params.value) return ''
          if (!Array.isArray(params.value)) return ''

          return (
            <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
              {params.value?.map((row, index) => (
                <div key={index}>{row}</div>
              ))}
            </div>
          )
        },
      },
    ],
    [getCompanyIcon, filterApplied, leftAlignCell],
  )

  const onFilterChanged = (params) => {
    if (!gridApi.current) return
    if (!_isMounted.current) return

    let text = gridApi.current.getGridOption('quickFilterText')
    setFilterApplied(Object.keys(gridApi.current.getFilterModel()).length > 0 || (text && text.length > 0))
  }

  const clearSelectedGroup = () => {
    if (gridApi.current && !gridApi.current.isDestroyed()) {
      gridApi.current.deselectAll()
    }

    setSelectedGroup(null)
  }

  const onSelectionChanged = useCallback((event) => {
    const nodes = event.api.getSelectedNodes()

    if (!nodes?.length) {
      setSelectedGroup(null)
      return
    }

    const node = nodes[0]

    setSelectedGroup(node.group ? null : (node.data ?? null))
  }, [])

  const handleUpdateGroup = async (form) => {
    const res = await updateAlertGroup(form)
    showResult(res)

    if (!res.success) return

    await fetchAlertGroups(organization)
    clearSelectedGroup()
    setGroupModal({ open: false, mode: 'add', data: null })
  }

  const handleCreateAlertGroup = async (form) => {
    const res = await addAlertGroup(form)
    showResult(res)

    if (!res.success) return

    await fetchAlertGroups(organization)
    clearSelectedGroup()
    setGroupModal({ open: false, mode: 'add', data: null })
  }

  const onDelete = (group) => {
    if (!group?.uid) return

    setConfirm({
      show: true,
      title: 'Delete Alert Group',
      text: `Are you sure you want to delete ${group.groupName}?`,
      onConfirm: () => handleDeleteAlertGroup(group.uid),
    })
  }

  const handleDeleteAlertGroup = async (uid) => {
    if (!uid || isDeleting) return

    const res = await deleteAlertGroup(uid)
    showResult(res)

    if (!res.success) return

    clearSelectedGroup()
    await fetchAlertGroups(organization)
  }

  return (
    <Dialog
      fullWidth
      maxWidth='xl'
      PaperProps={{
        sx: {
          backgroundColor: getWindowBarColor(),
          height: '80vh',
          width: '80vw',
          maxWidth: '95vw',
        },
      }}
      open={true}
      onClose={() => onCancel()}>
      <Box
        sx={{
          backgroundColor: getActionBarColor(),
          height: `calc(100vh)`,
          width: `calc(100% )`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}>
        {confirm.show ? (
          <ConfirmDialog
            title={confirm?.title}
            open={confirm?.show}
            setOpen={() => setConfirm({ show: false })}
            onConfirm={confirm.onConfirm}>
            {confirm?.text}
          </ConfirmDialog>
        ) : null}
        <Box
          sx={{
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            color: appColors.headerTextColor,
            backgroundColor: getWindowBarColor(),
            borderBottom: '1px solid rgba(255,255,255,0.12)',
          }}>
          <Typography sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Alert Groups
          </Typography>

          <IconButton onClick={onCancel} size='small' sx={{ ml: 1 }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        {groupModal.open && (
          <AddEditAlertGroupsModal
            open={groupModal.open}
            mode={groupModal.mode}
            initialData={groupModal.data}
            onClose={() => setGroupModal({ open: false, mode: 'add', data: null })}
            onSubmit={groupModal.mode === 'edit' ? handleUpdateGroup : handleCreateAlertGroup}
            getUnitsForOrg={getUnitsForOrg}
            organization={organization}
            showSpinner={isAdding || isUpdating}
            existingGroups={alertGroups}
            rigs={[{ label: 'All', value: 'All' }, ...(dropDowns?.rigs || [{ label: 'All', value: 'All' }])]}
            operators={[{ label: 'All', value: 'All' }, ...(dropDowns?.operators || [{ label: 'All', value: 'All' }])]}
          />
        )}
        {showAlerts ? (
          <AlertsModal
            onCancel={() => setShowAlerts(false)}
            userName={selectedGroup?.groupName ? selectedGroup?.groupName : ''}
            alertGroupAlerts={selectedGroup?.groupDetails?.alerts}
            alertGroup={selectedGroup}
            alertGroups={alertGroups}
          />
        ) : null}
        <Box sx={{ p: 1 }}>
          <AlertGroupRibbon
            selectedGroup={selectedGroup}
            onRefresh={() => fetchAlertGroups(organization)}
            onAddGroup={() => setGroupModal({ open: true, mode: 'add', data: null })}
            onEditGroup={() => setGroupModal({ open: true, mode: 'edit', data: selectedGroup })}
            onDeleteGroup={onDelete}
            isDeleting={isDeleting}
            userRole={userRole}
            onAlerts={() => setShowAlerts(true)}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: getBackColor(),
            padding: '2px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}>
          <SearchBarWithRecent
            value={searchText}
            onChange={(newSearchTerm) => setSearchText(newSearchTerm)}
            onCancelSearch={() => setSearchText('')}
            style={searchBarStyle()}
            recentKey='usersPage'
          />
        </Box>
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
          <div className={getAgGridTheme()} style={{ width: '100%', height: '100%' }}>
            <AgGridReact
              loading={isLoading || isDeleting || isAdding || isUpdating}
              rowData={alertGroups}
              columnDefs={sortColDefs(columnDefs, LS_KEY)}
              defaultColDef={defaultColDef}
              getRowId={(params) => String(params.data.uid)}
              animateRows={true}
              gridOptions={gridOptions}
              onGridReady={(params) => {
                gridApi.current = params.api
              }}
              onFirstDataRendered={onFirstDataRendered}
              getContextMenuItems={getContextMenuItems}
              headerHeight={30}
              onFilterChanged={onFilterChanged}
              onSelectionChanged={onSelectionChanged}
            />
          </div>
        </Box>
        <StatusSnackbar status={status} setStatus={setStatus} />
      </Box>
    </Dialog>
  )
}

export default AlertGroupsModal
